import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Fab,
    IconButton,
    Link as MuiLink,
    Paper,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    Tooltip,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import {
    GET_BILLING_SUBSCRIPTION,
    GET_BOOKINGS,
    GET_BUSINESS_AND_RELATED_BY_ID,
    GET_INVITEE_QUESTIONS,
    GET_PLANS,
} from "../../../../../platform/graphql/queries";
import { ADD_BOOKING_TYPE } from "../../../../../platform/graphql/mutations/bookingType";
import { ADD_BOOKINGS } from "../../../../../platform/graphql/mutations/generic";
import { SEND_EMAIL_MUTATION } from "../../../../../platform/graphql/mutations/business";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import { BookingStatus, getEffectivePlan } from "../../../../../platform/util/utils";
import { expandBookings } from "../../../../../platform/util/booking/expandBookings";
import {
    BOT_SYSTEM_PROMPT,
    CUSTOMER_HEADER_MAIN_BG,
    INLINE_DOCKED_PANEL_WIDTH,
    INLINE_DOCKED_PANEL_RESERVED_WIDTH,
    REQUIRED_PREMIUM_PLAN_LEVEL,
    STARTER_PROMPTS,
} from "./constants";
import {
    parseReplyContent,
    buildPlanIntentReply,
    isTodaysAppointmentsQuestion,
    isTodaysBirthdayQuestion,
    countTodaysAppointments,
    countTodaysBirthdays,
    buildTodaysAppointmentsReply,
    buildTodaysBirthdayReply,
    isCalendarNavigationQuestion,
    buildCalendarNavigationReply,
    resolveTabNavigationTarget,
    clickTabByTarget,
    resolveNavigationTarget,
    buildNavigationReply,
    isFollowUpNavigationRequest,
    isConfirmCreateBookingType,
    isCreateBookingTypeIntent,
    isBookingTypeDraftRefinement,
    applyBookingTypeDraftRefinement,
    parseSimpleBookingTypeDraft,
    isBookForMeQuestion,
    isExpiredDocumentsQuestion,
    buildExpiredDocumentsReply,
    isPageScopeQuestion,
    buildScopedPageReply,
    isWorkflowQuestion,
    findWorkflowMatch,
    buildWorkflowReply,
    buildRuntimeContext,
    splitMessageLinks,
    buildBookingTypeSlug,
    parseDurationMinutes,
    buildBookingTypeModePrompt,
    buildBookingTypeReadyReply,
    buildInviteBookingTypeChoiceReply,
} from "./helpers";
import {
    buildBookingTypeChoicePrompt,
    findCurrentLoginStaff,
    getOpenAIClient,
    isCancelBookForMeIntent,
    isConfirmBookForMeIntent,
    isTimezoneCreateError,
    mapAvailableBookingTypes,
    normalizeTimezoneId,
    parseBookingTypeChoice,
    PlanyBotSparkIcon,
    simplifyBookingCreateError,
    uniqueNonEmpty,
} from "./planyBotShared";

import {
    DOC_RESULT_LIMIT,
    toStartOfDay,
    isExpiryMatchingWindow,
    parseDocumentWindowDays,
    toDocFileName,
    formatDocDate,
    toGraphqlId,
    formatYmd,
    parseDateFromMessage,
    parseTimeFromMessage,
    parseDurationFromMessage,
    parseAvailabilityDaysFromMessage,
    parseBufferMinutesFromMessage,
    isConflictCheckQuestion,
    parseWindowDaysFromMessage,
    formatDateTimeLabel,
    rangesOverlap,
    parseTitleFromMessage,
    buildSuggestedBookingDescription,
    buildSaferCustomBookingDescription,
    reviseDescriptionWithInstruction,
    BOOKING_TITLE_OPTIONS,
    BOOKING_DURATION_OPTIONS,
    BOOKING_DAYS_OPTIONS,
    BOOKING_START_OPTIONS,
    BOOKING_END_OPTIONS,
    BOOKING_BUFFER_OPTIONS,
    pickOptionByNumber,
    buildNumberedQuestion,
    parseStructuredAvailabilityFromText,
    applyReadyDraftOverrides,
} from "./planyBotFlowUtils";

const GET_BOT_BUSINESS_DOCUMENTS = gql`
    query GetBusinessDocuments($businessId: ID!) {
        getBusinessDocuments(businessId: $businessId) {
            id
            fileName
            fileUrl
            expiresAt
        }
    }
`;

const GET_BOT_RESIDENT_DOCUMENTS = gql`
    query GetResidentDocuments($residentId: ID!) {
        getResidentDocuments(residentId: $residentId) {
            id
            fileName
            fileUrl
            expiresAt
        }
    }
`;

const GET_BOT_STAFF_DOCUMENTS = gql`
    query GetStaffDocuments($staffId: ID!) {
        getStaffDocuments(staffId: $staffId) {
            id
            fileName
            fileUrl
            expiresAt
        }
    }
`;

const GET_BOT_RESIDENT_BIRTHDAYS = gql`
    query GetResidents($businessId: ID!) {
        getResidents(businessId: $businessId) {
            id
            firstName
            lastName
            dateOfBirth
        }
    }
`;

const GET_BOT_BADGE_SUMMARY = gql`
    query GetBotBadgeSummary($businessId: ID!) {
        getBotBadgeSummary(businessId: $businessId) {
            todaysBirthdaysCount
            expiredDocsCount
            pastDueMedicationCount
        }
    }
`;

export default function PlanyBot({ inline = false }) {
    const { providerUser } = useContext(UserContext);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const selectedBusiness = useSelectedBusiness();
    const businessId = selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId;
    const numericBusinessId = Number(businessId || 0) || null;
    const isInline = Boolean(inline);
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const [isOpen, setIsOpen] = useState(false);
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const [pendingCreateBookingType, setPendingCreateBookingType] = useState(null);
    const [pendingBookForMeDraft, setPendingBookForMeDraft] = useState(null);
    const [pendingInviteLinkDraft, setPendingInviteLinkDraft] = useState(null);
    const [pendingTabNavigation, setPendingTabNavigation] = useState(null);
    const [lastTabNavigationTarget, setLastTabNavigationTarget] = useState(null);
    const [lastPageNavigationTarget, setLastPageNavigationTarget] = useState(null);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [promptBadgeCounts, setPromptBadgeCounts] = useState({
        birthdays: 0,
        expiredDocs: 0,
        pastDueMedication: 0,
    });
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "Hi, I'm planyBot. Tell me what you want to do or find, and I can guide you.",
        },
    ]);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const hasPerformedInitialBadgeSyncRef = useRef(false);
    const lastBadgeSyncBusinessIdRef = useRef(null);

    const { data: billingData, loading: billingLoading } = useQuery(GET_BILLING_SUBSCRIPTION, {
        variables: { businessId },
        skip: !businessId,
        fetchPolicy: "network-only",
    });

    const selectedYear = new Date().getFullYear();
    const { data: bookingsData, loading: bookingsLoading, refetch: refetchBookings } = useQuery(
        GET_BOOKINGS,
        {
            variables: {
                businessId: numericBusinessId,
                staffId: providerUser?.staffId ? Number(providerUser.staffId) : null,
                selectedYear,
            },
            skip: !numericBusinessId,
            fetchPolicy: "cache-and-network",
            nextFetchPolicy: "cache-first",
        }
    );

    const { data: plansData } = useQuery(GET_PLANS);
    const [createQuickBookingType, { loading: createBookingTypeLoading }] = useMutation(
        ADD_BOOKING_TYPE
    );
    const [createBookingByBot, { loading: createBookingByBotLoading }] = useMutation(ADD_BOOKINGS);
    const [sendInviteEmail] = useMutation(SEND_EMAIL_MUTATION);

    const hasServerAi = false;
    const subscription = billingData?.getBillingSubscription;
    const premiumPlanId = plansData?.plans?.find((plan) => plan.code === "PREMIUM")?.id;
    const effectivePlanLevel = Number(getEffectivePlan(providerUser) || 0);
    const hasTokenPremium = effectivePlanLevel >= REQUIRED_PREMIUM_PLAN_LEVEL;
    const subscriptionPlanCode = useMemo(() => {
        if (!subscription?.planId || !Array.isArray(plansData?.plans)) return null;
        const matchedPlan = plansData.plans.find(
            (plan) => String(plan.id) === String(subscription.planId)
        );
        return matchedPlan?.code || null;
    }, [plansData?.plans, subscription?.planId]);
    const isPremiumPlan = Boolean(
        subscriptionPlanCode === "PREMIUM" ||
        (premiumPlanId != null &&
            String(subscription?.planId) === String(premiumPlanId)) ||
        Number(subscription?.planId) >= REQUIRED_PREMIUM_PLAN_LEVEL
    );
    const checkingPremium = Boolean(businessId) && billingLoading && !hasTokenPremium;
    const hasBusinessContext = Boolean(businessId);
    const hasBusinessPremium = Boolean(
        subscription &&
        subscription?.status !== "canceled" &&
        isPremiumPlan
    );
    const hasAssistantAccess = hasTokenPremium || hasBusinessPremium;
    const shouldRequireUpgrade = Boolean(
        !hasAssistantAccess && (hasBusinessContext ? !checkingPremium : true)
    );
    const disableInput =
        !hasServerAi ||
        isLoading ||
        createBookingTypeLoading ||
        createBookingByBotLoading ||
        checkingPremium ||
        shouldRequireUpgrade;
    const availableBookingTypes = useMemo(
        () => mapAvailableBookingTypes(selectedBusiness),
        [selectedBusiness]
    );
    const currentLoginStaff = useMemo(
        () => findCurrentLoginStaff(selectedBusiness, providerUser),
        [selectedBusiness, providerUser]
    );
    const isInviteSenderAdmin =
        String(currentLoginStaff?.role?.name || "").trim().toUpperCase() === "ADMIN";
    const expandedBookings = useMemo(
        () =>
            expandBookings(
                Array.isArray(bookingsData?.getBookings) ? bookingsData.getBookings : []
            ),
        [bookingsData?.getBookings]
    );
    const todaysAppointmentsCount = useMemo(
        () => countTodaysAppointments(expandedBookings, providerUser),
        [expandedBookings, providerUser]
    );
    const localTodaysBirthdayCount = useMemo(
        () =>
            countTodaysBirthdays(
                Array.isArray(selectedBusiness?.residents) ? selectedBusiness.residents : [],
                Array.isArray(selectedBusiness?.businessStaffs) ? selectedBusiness.businessStaffs : []
            ),
        [selectedBusiness?.residents, selectedBusiness?.businessStaffs]
    );

    const canSend = useMemo(() => input.trim().length > 0 && !disableInput, [input, disableInput]);
    const useDockedPanel = isInline && !isSmallScreen;
    const useCompactInlinePanel = isInline && isSmallScreen;

    const buildCrossPageExpiredDocsReply = async (userMessage, runtimeContext = {}, options = {}) => {
        const asMeta = Boolean(options?.asMeta);
        if (!isExpiredDocumentsQuestion(userMessage)) return "";
        if (!numericBusinessId) {
            if (asMeta) {
                return {
                    business: 0,
                    resident: 0,
                    staff: 0,
                    total: 0,
                    checkedResidents: 0,
                    checkedStaffs: 0,
                    hadQueryErrors: false,
                    upcomingWindowDays: null,
                };
            }
            return "I could not check expired documents because no business is selected.";
        }

        const windowQuery = parseDocumentWindowDays(userMessage);
        if (windowQuery.requested && windowQuery.days === null) {
            if (asMeta) {
                return {
                    business: 0,
                    resident: 0,
                    staff: 0,
                    total: 0,
                    checkedResidents: 0,
                    checkedStaffs: 0,
                    hadQueryErrors: false,
                    upcomingWindowDays: null,
                };
            }
            return "Tell me the number of days to check, for example: Show docs expiring in next 7 days.";
        }
        const upcomingWindowDays = windowQuery.days;
        const todayStart = toStartOfDay(new Date());
        const windowEnd =
            upcomingWindowDays != null
                ? toStartOfDay(new Date(todayStart.getTime() + (upcomingWindowDays * 24 * 60 * 60 * 1000)))
                : null;

        let residents = Array.isArray(selectedBusiness?.residents)
            ? selectedBusiness.residents
            : [];
        let staffs = Array.isArray(selectedBusiness?.businessStaffs)
            ? selectedBusiness.businessStaffs
            : [];

        if (!residents.length || !staffs.length) {
            try {
                const { data } = await apolloClient.query({
                    query: GET_BUSINESS_AND_RELATED_BY_ID,
                    variables: { id: numericBusinessId },
                    fetchPolicy: "network-only",
                });
                const business = data?.business;
                if (!residents.length) {
                    residents = Array.isArray(business?.residents) ? business.residents : residents;
                }
                if (!staffs.length) {
                    staffs = Array.isArray(business?.staffs) ? business.staffs : staffs;
                }
            } catch (error) {
                // Fallback to currently loaded store values only.
            }
        }

        const expiredDocs = [];
        const expiredCounts = {
            business: 0,
            resident: 0,
            staff: 0,
        };
        const seen = new Set();
        let hadQueryErrors = false;

        const addExpiredDocuments = (docs, sectionLabel, scope) => {
            (Array.isArray(docs) ? docs : []).forEach((doc) => {
                const matchesQuery = isExpiryMatchingWindow(
                    doc?.expiresAt,
                    todayStart,
                    windowEnd,
                    upcomingWindowDays
                );
                if (!matchesQuery) return;
                const fileName = toDocFileName(doc);
                const key = `${sectionLabel}::${fileName}`;
                if (seen.has(key)) return;
                seen.add(key);
                if (scope && Object.prototype.hasOwnProperty.call(expiredCounts, scope)) {
                    expiredCounts[scope] += 1;
                }
                expiredDocs.push({
                    fileName,
                    scope: scope || null,
                    sectionLabel,
                    expiresAt: doc?.expiresAt || null,
                });
            });
        };

        try {
            const businessResult = await apolloClient.query({
                query: GET_BOT_BUSINESS_DOCUMENTS,
                variables: { businessId: numericBusinessId },
                fetchPolicy: "network-only",
            });
            addExpiredDocuments(
                businessResult?.data?.getBusinessDocuments || [],
                "Business Documents",
                "business"
            );
        } catch (error) {
            hadQueryErrors = true;
        }

        const residentRequests = residents
            .map((resident) => {
                const residentId = toGraphqlId(resident?.id);
                if (!residentId) return null;
                const residentName = [
                    String(resident?.firstName || "").trim(),
                    String(resident?.lastName || "").trim(),
                ]
                    .filter(Boolean)
                    .join(" ");
                const sectionLabel = `Resident: ${residentName || `#${residentId}`}`;
                return apolloClient
                    .query({
                        query: GET_BOT_RESIDENT_DOCUMENTS,
                        variables: { residentId },
                        fetchPolicy: "network-only",
                    })
                    .then((result) => {
                        addExpiredDocuments(
                            result?.data?.getResidentDocuments || [],
                            sectionLabel,
                            "resident"
                        );
                    })
                    .catch(() => {
                        hadQueryErrors = true;
                    });
            })
            .filter(Boolean);

        const staffRequests = staffs
            .map((staff) => {
                const staffId = toGraphqlId(staff?.id);
                if (!staffId) return null;
                const staffName =
                    String(staff?.name || "").trim() ||
                    String(staff?.user?.username || "").trim() ||
                    String(staff?.user?.email || "").trim();
                const sectionLabel = `Staff: ${staffName || `#${staffId}`}`;
                return apolloClient
                    .query({
                        query: GET_BOT_STAFF_DOCUMENTS,
                        variables: { staffId },
                        fetchPolicy: "network-only",
                    })
                    .then((result) => {
                        addExpiredDocuments(
                            result?.data?.getStaffDocuments || [],
                            sectionLabel,
                            "staff"
                        );
                    })
                    .catch(() => {
                        hadQueryErrors = true;
                    });
            })
            .filter(Boolean);

        await Promise.all([...residentRequests, ...staffRequests]);

        const byDate = (value) => {
            if (!value) return Number.POSITIVE_INFINITY;
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
        };

        expiredDocs.sort((a, b) => {
            const dateDiff = byDate(a.expiresAt) - byDate(b.expiresAt);
            if (dateDiff !== 0) return dateDiff;
            const sectionDiff = a.sectionLabel.localeCompare(b.sectionLabel);
            if (sectionDiff !== 0) return sectionDiff;
            return a.fileName.localeCompare(b.fileName);
        });

        const totalExpired =
            expiredCounts.business + expiredCounts.resident + expiredCounts.staff;
        const checkedSummary = `Checked: Business documents, ${residents.length} resident profile(s), ${staffs.length} staff profile(s).`;
        const countsSummary = upcomingWindowDays == null
            ? `Expired counts: Business ${expiredCounts.business}, Resident ${expiredCounts.resident}, Staff ${expiredCounts.staff}, Total ${totalExpired}.`
            : `Expiring in next ${upcomingWindowDays} day(s) (including today): Business ${expiredCounts.business}, Resident ${expiredCounts.resident}, Staff ${expiredCounts.staff}, Total ${totalExpired}.`;
        const responseMeta = {
            business: expiredCounts.business,
            resident: expiredCounts.resident,
            staff: expiredCounts.staff,
            total: totalExpired,
            checkedResidents: residents.length,
            checkedStaffs: staffs.length,
            hadQueryErrors,
            upcomingWindowDays,
        };
        if (asMeta) {
            return responseMeta;
        }
        const sectionOrder = ["business", "resident", "staff"];
        const sectionMeta = {
            business: {
                label: "Business Documents",
                href: "/biz/business#documents",
            },
            resident: {
                label: "Resident Documents",
                href: "/biz/resident#documents",
            },
            staff: {
                label: "Staff Documents",
                href: "/biz/staff#document",
            },
        };
        const inferScopeFromSectionLabel = (sectionLabel = "") => {
            const value = String(sectionLabel || "");
            if (value.startsWith("Resident:")) return "resident";
            if (value.startsWith("Staff:")) return "staff";
            return "business";
        };
        const limitedExpiredDocs = expiredDocs.slice(0, DOC_RESULT_LIMIT);
        const groupedExpiredDocs = {
            business: [],
            resident: [],
            staff: [],
        };
        limitedExpiredDocs.forEach((doc) => {
            const scopeKey =
                doc.scope && Object.prototype.hasOwnProperty.call(groupedExpiredDocs, doc.scope)
                    ? doc.scope
                    : inferScopeFromSectionLabel(doc.sectionLabel);
            groupedExpiredDocs[scopeKey].push(doc);
        });
        const formatGroupedDocLine = (doc, scopeKey) => {
            const expiresText = formatDocDate(doc.expiresAt);
            if (scopeKey === "business") {
                return `  - ${doc.fileName}${expiresText ? ` (expires ${expiresText})` : ""}`;
            }
            return `  - ${doc.fileName} (${doc.sectionLabel}${expiresText ? `; expires ${expiresText}` : ""})`;
        };
        const groupedDocumentSections = ["Open document tabs:"];
        sectionOrder.forEach((scopeKey) => {
            const meta = sectionMeta[scopeKey];
            const group = groupedExpiredDocs[scopeKey];
            const linkCountText = upcomingWindowDays == null
                ? `${expiredCounts[scopeKey]} expired`
                : `${expiredCounts[scopeKey]} in ${upcomingWindowDays} day(s)`;
            groupedDocumentSections.push(
                `- [${meta.label} (${linkCountText})](${meta.href})`
            );
            if (!group.length) {
                groupedDocumentSections.push("  - None");
                return;
            }
            group.forEach((doc) => {
                groupedDocumentSections.push(formatGroupedDocLine(doc, scopeKey));
            });
        });

        if (!expiredDocs.length) {
            return [
                upcomingWindowDays == null
                    ? "I checked across Business, Resident, and Staff documents and found no expired documents."
                    : `I checked across Business, Resident, and Staff documents and found no documents expiring in the next ${upcomingWindowDays} day(s).`,
                countsSummary,
                checkedSummary,
                ...groupedDocumentSections,
                hadQueryErrors
                    ? "Some profiles could not be checked due to a temporary data fetch error."
                    : "",
            ]
                .filter(Boolean)
                .join("\n");
        }

        const lines = [
            upcomingWindowDays == null
                ? "Expired document summary across Business, Resident, and Staff:"
                : `Document expiry summary for next ${upcomingWindowDays} day(s) across Business, Resident, and Staff:`,
            countsSummary,
            checkedSummary,
            ...groupedDocumentSections,
        ];

        if (expiredDocs.length > DOC_RESULT_LIMIT) {
            lines.push(
                `+ ${expiredDocs.length - DOC_RESULT_LIMIT} more ${upcomingWindowDays == null ? "expired" : "matching"} document(s) not listed.`
            );
        }

        if (hadQueryErrors) {
            lines.push("Some profiles could not be checked due to a temporary data fetch error.");
        }
        return lines.join("\n");
    };

    const runCreateSimpleBookingType = async (draft) => {
        if (!numericBusinessId) {
            return "I cannot create a booking type yet because no business is selected.";
        }
        const createdBy = Number(providerUser?.staffId || providerUser?.userId || 0);
        if (!createdBy) {
            return "I cannot create a booking type because your user context is missing createdBy.";
        }

        const staffList = Array.isArray(selectedBusiness?.businessStaffs)
            ? selectedBusiness.businessStaffs
            : [];
        const primaryStaff =
            staffList.find((staff) => Number(staff?.id) === Number(providerUser?.staffId)) ||
            staffList[0];

        if (!primaryStaff?.id) {
            return "I could not find an assignable staff profile. Please create/open a staff first.";
        }

        const groupId = primaryStaff?.workingHourGroups?.[0]?.id;
        const assignedStaffs = [
            {
                staffId: Number(primaryStaff.id),
                ...(groupId ? { groupId: Number(groupId) } : {}),
            },
        ];

        const now = new Date();
        now.setSeconds(0, 0);
        const start = now.toISOString();
        const end = new Date(now.getTime() + 60 * 60 * 1000).toISOString();

        const payload = {
            title: draft.title,
            start,
            end,
            location: draft.location || "No location",
            meetingLocationTypeId: draft.meetingLocationTypeId || "4",
            desc:
                [
                    draft.desc ||
                    `${draft.title} booking. Please select a suitable time slot.`,
                    (Array.isArray(draft?.availabilityDays) &&
                        draft.availabilityDays.length &&
                        draft?.availabilityStartLabel &&
                        draft?.availabilityEndLabel)
                        ? `Requested availability: ${draft.availabilityDays.join(", ")} from ${draft.availabilityStartLabel} to ${draft.availabilityEndLabel}`
                        : "",
                    draft?.availabilityNotes
                        ? `Requested availability notes: ${draft.availabilityNotes}`
                        : "",
                    Number.isFinite(Number(draft?.bufferMinutes))
                        ? `Requested buffer: ${Number(draft.bufferMinutes)} minutes`
                        : "",
                ]
                    .filter(Boolean)
                    .join("\n\n"),
            color: draft.color || "#2563eb",
            link: buildBookingTypeSlug(draft.title),
            length: Number(draft.durationMinutes || 30),
            price: 0,
            phoneNumber: "",
            bookingTypeCategoryId: Number(draft.bookingTypeCategoryId || 2),
            assignedStaffs,
            createdBy,
            businessId: Number(numericBusinessId),
            canInviteeCall: false,
        };

        try {
            const res = await createQuickBookingType({ variables: payload });
            const created = res?.data?.addBookingType;
            if (!created?.id) {
                return "I tried creating it, but did not get a valid booking type id back.";
            }
            if (typeof refetchBookings === "function") {
                refetchBookings();
            }
            navigate(`/biz/update/${created.id}`);
            return [
                `Created booking type "${created.title}" successfully.`,
                "I opened its edit page so you can adjust details and publish.",
            ].join("\n");
        } catch (error) {
            return `I could not create the booking type: ${error?.message || "Unknown error"}`;
        }
    };

    const runBookForMeFlow = async (userMessage) => {
        const text = String(userMessage || "");
        const hasIntent = Boolean(pendingBookForMeDraft) || isBookForMeQuestion(text);
        if (!hasIntent) return "";

        if (isCancelBookForMeIntent(text)) {
            setPendingBookForMeDraft(null);
            return "Canceled appointment creation.";
        }

        if (!numericBusinessId) {
            return "I cannot create an appointment because no business is selected.";
        }
        if (!availableBookingTypes.length) {
            return "I could not find active booking types for this business. Please create one first.";
        }

        const isNewIntentMessage = isBookForMeQuestion(text);
        const shouldResetForNewIntent =
            isNewIntentMessage &&
            !isConfirmBookForMeIntent(text) &&
            !isCancelBookForMeIntent(text);

        const draft = shouldResetForNewIntent || !pendingBookForMeDraft
            ? {
                bookingTypeId: null,
                bookingTypeTitle: "",
                dateYmd: "",
                hour: null,
                minute: null,
                timeLabel: "",
                awaitingFinalConfirm: false,
            }
            : { ...pendingBookForMeDraft };

        const selectedBookingType = parseBookingTypeChoice(text, availableBookingTypes);
        if (!draft.bookingTypeId && selectedBookingType?.id) {
            draft.bookingTypeId = Number(selectedBookingType.id);
            draft.bookingTypeTitle = selectedBookingType.title;
        }

        if (!draft.dateYmd) {
            draft.dateYmd = parseDateFromMessage(text);
        }
        if (!draft.timeLabel) {
            const parsedTime = parseTimeFromMessage(text);
            if (parsedTime) {
                draft.hour = parsedTime.hour;
                draft.minute = parsedTime.minute;
                draft.timeLabel = parsedTime.label;
            }
        }

        const submitDraft = async (finalDraft) => {
            const scheduled = new Date(`${finalDraft.dateYmd}T00:00:00`);
            scheduled.setHours(Number(finalDraft.hour), Number(finalDraft.minute), 0, 0);
            if (Number.isNaN(scheduled.getTime())) {
                setPendingBookForMeDraft(finalDraft);
                return "I could not parse that date/time. Use format: 2026-02-19 2:30 PM.";
            }

            const matchedType = availableBookingTypes.find(
                (item) => Number(item.id) === Number(finalDraft.bookingTypeId)
            );
            try {
                let inviteeAnswers = [];
                try {
                    const inviteeRes = await apolloClient.query({
                        query: GET_INVITEE_QUESTIONS,
                        variables: {
                            bookingTypeId: Number(finalDraft.bookingTypeId),
                            staffId: providerUser?.staffId ? Number(providerUser.staffId) : null,
                        },
                        fetchPolicy: "network-only",
                    });
                    const questions = Array.isArray(inviteeRes?.data?.getInviteeQuestions)
                        ? inviteeRes.data.getInviteeQuestions
                        : [];
                    inviteeAnswers = questions
                        .filter((q) => Boolean(q?.required))
                        .map((q) => {
                            const title = String(q?.title || "").toLowerCase();
                            let answer = "";
                            if (title.includes("email")) {
                                answer = String(providerUser?.email || "").trim();
                            } else if (title.includes("name")) {
                                answer = String(providerUser?.username || providerUser?.email || "Plany User").trim();
                            } else if (title.includes("phone")) {
                                answer = String(
                                    providerUser?.phone ||
                                    providerUser?.phoneNumber ||
                                    providerUser?.mobile ||
                                    ""
                                ).trim();
                            } else {
                                answer = "Booked by planyBot";
                            }
                            return {
                                questionId: Number(q.id),
                                answer: answer || "Booked by planyBot",
                            };
                        });
                } catch (_inviteeError) {
                    // If fetching invitee questions fails, continue with empty answers.
                }

                const staffUrl =
                    matchedType?.assignedStaffs?.[0]?.url ||
                    providerUser?.staffUrl ||
                    providerUser?.url ||
                    "";
                const businessUrl =
                    selectedBusiness?.businessBasic?.url ||
                    selectedBusiness?.url ||
                    providerUser?.businessUrl ||
                    "";
                const basePrice = Number(matchedType?.price || 0) || 0;
                const discountPct = Number(matchedType?.discount || 0) || 0;
                const finalPrice = Math.max(0, basePrice - basePrice * (discountPct / 100));
                const rawProviderTz = providerUser?.timeZone?.timezone;
                const rawBusinessTz = selectedBusiness?.businessBasic?.timezone;
                const rawLocalStorageTz =
                    typeof window !== "undefined" ? window.localStorage?.getItem("timezone") : "";
                const rawBrowserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

                // Try exact saved values first (same behavior as existing booking flow), then normalized fallbacks.
                const tzCandidates = uniqueNonEmpty([
                    rawProviderTz,
                    rawBusinessTz,
                    rawLocalStorageTz,
                    rawBrowserTz,
                    normalizeTimezoneId(rawProviderTz),
                    normalizeTimezoneId(rawBusinessTz),
                    normalizeTimezoneId(rawLocalStorageTz),
                    normalizeTimezoneId(rawBrowserTz),
                    "America/Los_Angeles",
                    "UTC",
                ]);

                let lastCreateError = null;
                let created = false;
                for (const tz of tzCandidates) {
                    try {
                        await createBookingByBot({
                            variables: {
                                input: {
                                    bookingTypeId: Number(finalDraft.bookingTypeId),
                                    timezone: tz,
                                    bookingId: `bot-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
                                    time: scheduled.toISOString(),
                                    status: matchedType?.bookingApproval ? BookingStatus.PENDING : BookingStatus.ACTIVE,
                                    answers: inviteeAnswers,
                                    staffUrl,
                                    businessUrl,
                                    finalPrice,
                                    reminderMinutes: 60,
                                },
                            },
                        });
                        created = true;
                        break;
                    } catch (innerError) {
                        lastCreateError = innerError;
                        if (!isTimezoneCreateError(innerError)) {
                            throw innerError;
                        }
                    }
                }

                if (!created && lastCreateError) {
                    throw lastCreateError;
                }
                setPendingBookForMeDraft(null);
                if (typeof refetchBookings === "function") {
                    refetchBookings();
                }
                return [
                    `Appointment created: ${finalDraft.bookingTypeTitle || "Booking"} on ${finalDraft.dateYmd} at ${finalDraft.timeLabel}.`,
                    "Open /biz/calendar to review it.",
                ].join("\n");
            } catch (error) {
                setPendingBookForMeDraft(finalDraft);
                const detail = simplifyBookingCreateError(error);
                return `I could not create the appointment: ${detail}`;
            }
        };

        if (pendingBookForMeDraft?.awaitingFinalConfirm && isConfirmBookForMeIntent(text)) {
            return submitDraft(draft);
        }
        if (pendingBookForMeDraft?.awaitingFinalConfirm && isCancelBookForMeIntent(text)) {
            setPendingBookForMeDraft(null);
            return "Canceled appointment creation.";
        }

        if (isConfirmBookForMeIntent(text) && !selectedBookingType?.id) {
            setPendingBookForMeDraft(draft);
            return "Select booking type/date/time first. Then reply `1` to confirm or `2` to cancel.";
        }

        if (!draft.bookingTypeId) {
            draft.awaitingFinalConfirm = false;
            setPendingBookForMeDraft(draft);
            return buildBookingTypeChoicePrompt(availableBookingTypes);
        }
        if (!draft.dateYmd && !draft.timeLabel) {
            draft.awaitingFinalConfirm = false;
            setPendingBookForMeDraft(draft);
            return `Selected booking type: ${draft.bookingTypeTitle}. Tell me date and time (example: 2026-02-19 2:30 PM).`;
        }
        if (!draft.dateYmd) {
            draft.awaitingFinalConfirm = false;
            setPendingBookForMeDraft(draft);
            return `Selected booking type: ${draft.bookingTypeTitle}. Tell me the date (example: 2026-02-19 or tomorrow).`;
        }
        if (!draft.timeLabel) {
            draft.awaitingFinalConfirm = false;
            setPendingBookForMeDraft(draft);
            return `Selected booking type: ${draft.bookingTypeTitle} on ${draft.dateYmd}. Tell me the time (example: 2:30 PM).`;
        }

        draft.awaitingFinalConfirm = true;
        setPendingBookForMeDraft(draft);
        return [
            "Please confirm appointment:",
            `- Booking type: ${draft.bookingTypeTitle}`,
            `- Date: ${draft.dateYmd}`,
            `- Time: ${draft.timeLabel}`,
            "Reply `1` to Confirm, or `2` to Cancel.",
        ].join("\n");
    };

    const runSendInviteLinkFlow = async (userMessage) => {
        const text = String(userMessage || "");
        const lower = text.toLowerCase();
        const sendInviteForType = async (recipient, selectedType, businessUrl) => {
            const root = window?.location?.origin || "";
            const publicLink = `${root}/b/${businessUrl}/booking/${selectedType.link}`;
            const subject = `Booking link: ${selectedType.title}`;
            const textBody = [
                `Hello,`,
                ``,
                `You can book using this link:`,
                publicLink,
                ``,
                `Thank you.`,
            ].join("\n");

            try {
                await sendInviteEmail({
                    variables: {
                        from_email: String(providerUser?.email || "").trim() || undefined,
                        to: recipient,
                        subject,
                        text: textBody,
                        html: `<p>Hello,</p><p>You can book using this link:</p><p><a href="${publicLink}">${publicLink}</a></p><p>Thank you.</p>`,
                    },
                });
                return `Invite link sent to ${recipient} for "${selectedType.title}".\nLink: ${publicLink}`;
            } catch (_error) {
                return [
                    `I could not send email right now, but here is the booking link for ${recipient}:`,
                    publicLink,
                ].join("\n");
            }
        };

        if (pendingInviteLinkDraft) {
            if (!isInviteSenderAdmin) {
                setPendingInviteLinkDraft(null);
                return "Admin role required to send booking invite links.";
            }
            if (/^\s*2\s*$/.test(lower) || /^cancel$|cancel send|stop$/i.test(lower)) {
                setPendingInviteLinkDraft(null);
                return "Canceled invite link sending.";
            }
            const selected = parseBookingTypeChoice(text, pendingInviteLinkDraft.candidates || []);
            if (!selected?.link) {
                const options = (pendingInviteLinkDraft.candidates || []).slice(0, 8);
                return buildInviteBookingTypeChoiceReply(
                    pendingInviteLinkDraft.recipient,
                    options
                );
            }
            const reply = await sendInviteForType(
                pendingInviteLinkDraft.recipient,
                selected,
                pendingInviteLinkDraft.businessUrl
            );
            setPendingInviteLinkDraft(null);
            return reply;
        }

        const looksLikeInviteRequest =
            /(send|share|email).*(invite|link|booking)/.test(lower) ||
            /(invite|link).*(send|share|email)/.test(lower);
        if (!looksLikeInviteRequest) return "";
        if (!isInviteSenderAdmin) {
            return "Admin role required to send booking invite links.";
        }

        const emailMatch = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
        const recipient = String(emailMatch?.[0] || "").trim();
        if (!recipient) {
            return "Please provide the recipient email address. Example: send invite link to name@example.com";
        }

        const businessUrl = String(selectedBusiness?.businessBasic?.url || "").trim();
        if (!businessUrl) {
            return "I cannot send invite link because business URL is missing.";
        }

        const normalizedText = lower.replace(/[^a-z0-9\s]/g, " ");
        const shareableTypes = availableBookingTypes.filter((bt) => bt?.link);
        const requestedType = shareableTypes.find((bt) => {
            const title = String(bt?.title || "").toLowerCase();
            return title && normalizedText.includes(title);
        });

        if (!shareableTypes.length) {
            return "I could not find a booking type with a shareable link. Please create/publish one first.";
        }

        if (requestedType?.link) {
            return sendInviteForType(recipient, requestedType, businessUrl);
        }

        if (shareableTypes.length === 1) {
            return sendInviteForType(recipient, shareableTypes[0], businessUrl);
        }

        const options = shareableTypes.slice(0, 8);
        setPendingInviteLinkDraft({
            recipient,
            businessUrl,
            candidates: options,
        });
        return buildInviteBookingTypeChoiceReply(recipient, options);
    };
    const buildBookForMeReply = runBookForMeFlow;
    const buildBufferStepPrompt = () =>
        [
            buildNumberedQuestion(
                "Question 7/7: Add buffer time between meetings?",
                BOOKING_BUFFER_OPTIONS.map((m) => `${m} minutes`)
            ),
            "6. Edit schedule",
            "Reply with number (or type your own value).",
        ].join("\n");
    const buildConflictCheckReply = (userMessage) => {
        if (!isConflictCheckQuestion(userMessage)) return "";
        if (bookingsLoading) return "Loading schedule, please retry in a moment.";

        const currentStaffId = Number(providerUser?.staffId || 0);
        const currentEmail = String(providerUser?.email || "")
            .trim()
            .toLowerCase();
        const currentUserBookings = expandedBookings
            .filter((booking) => {
                const bookingStaffId = Number(booking?.staffId || 0);
                if (currentStaffId && bookingStaffId && bookingStaffId === currentStaffId) return true;
                const bookingStaffEmail = String(booking?.staffEmail || "")
                    .trim()
                    .toLowerCase();
                return Boolean(currentEmail && bookingStaffEmail && bookingStaffEmail === currentEmail);
            })
            .map((booking) => {
                const start = new Date(booking?.time);
                if (Number.isNaN(start.getTime())) return null;
                const durationMinutes = Math.max(1, Number(booking?.duration || 30) || 30);
                const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
                return {
                    start,
                    end,
                    bookingTypeName: String(booking?.bookingTypeName || "Booking").trim() || "Booking",
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.start.getTime() - b.start.getTime());

        if (!currentUserBookings.length) {
            return "I could not find schedule entries for your account to check conflicts.";
        }

        const requestedTime = parseTimeFromMessage(userMessage);
        if (requestedTime) {
            const requestedDate = parseDateFromMessage(userMessage) || formatYmd(new Date());
            const requestedStart = new Date(`${requestedDate}T00:00:00`);
            if (Number.isNaN(requestedStart.getTime())) {
                return "Please provide a valid date/time, for example: check conflict on 2026-03-01 at 5:30 PM.";
            }
            requestedStart.setHours(requestedTime.hour, requestedTime.minute, 0, 0);
            const requestedDuration = parseDurationFromMessage(userMessage) || 60;
            const requestedEnd = new Date(requestedStart.getTime() + requestedDuration * 60 * 1000);
            const overlaps = currentUserBookings.filter((entry) =>
                rangesOverlap(
                    requestedStart.getTime(),
                    requestedEnd.getTime(),
                    entry.start.getTime(),
                    entry.end.getTime()
                )
            );

            if (!overlaps.length) {
                return [
                    `No conflict found for ${formatDateTimeLabel(requestedStart)} (${requestedDuration} min).`,
                    "This checks your Plany schedule entries (including synced external events that are stored in bookings).",
                ].join("\n");
            }

            return [
                `Conflict detected for ${formatDateTimeLabel(requestedStart)} (${requestedDuration} min).`,
                ...overlaps.slice(0, 5).map(
                    (entry) =>
                        `- ${entry.bookingTypeName}: ${formatDateTimeLabel(entry.start)} to ${formatDateTimeLabel(entry.end)}`
                ),
                overlaps.length > 5 ? `+ ${overlaps.length - 5} more conflict(s).` : "",
            ]
                .filter(Boolean)
                .join("\n");
        }

        const windowDays = parseWindowDaysFromMessage(userMessage, 14);
        const windowStart = new Date();
        const windowEnd = new Date(windowStart.getTime() + windowDays * 24 * 60 * 60 * 1000);
        const upcoming = currentUserBookings.filter(
            (entry) => entry.start.getTime() >= windowStart.getTime() && entry.start.getTime() <= windowEnd.getTime()
        );

        const overlaps = [];
        for (let i = 0; i < upcoming.length; i += 1) {
            for (let j = i + 1; j < upcoming.length; j += 1) {
                if (upcoming[j].start.getTime() >= upcoming[i].end.getTime()) break;
                if (
                    rangesOverlap(
                        upcoming[i].start.getTime(),
                        upcoming[i].end.getTime(),
                        upcoming[j].start.getTime(),
                        upcoming[j].end.getTime()
                    )
                ) {
                    overlaps.push([upcoming[i], upcoming[j]]);
                    if (overlaps.length >= 8) break;
                }
            }
            if (overlaps.length >= 8) break;
        }

        if (!overlaps.length) {
            return `No overlapping bookings found in your next ${windowDays} day(s).`;
        }

        return [
            `Found ${overlaps.length} booking conflict(s) in your next ${windowDays} day(s):`,
            ...overlaps.map(
                ([left, right]) =>
                    `- ${formatDateTimeLabel(left.start)}: ${left.bookingTypeName} overlaps with ${right.bookingTypeName}`
            ),
            "Open /biz/calendar to adjust the conflicting slots.",
        ].join("\n");
    };

    useEffect(() => {
        const currentBusinessKey = String(numericBusinessId || "");
        if (lastBadgeSyncBusinessIdRef.current !== currentBusinessKey) {
            hasPerformedInitialBadgeSyncRef.current = false;
            lastBadgeSyncBusinessIdRef.current = currentBusinessKey;
        }
    }, [numericBusinessId]);

    useEffect(() => {
        let cancelled = false;
        let syncing = false;

        const runPromptBadgeSync = async () => {
            if (syncing) return;
            syncing = true;
            let nextBirthdays = localTodaysBirthdayCount;
            let nextExpiredDocs = 0;
            let nextPastDueMedication = 0;
            try {
                if (!numericBusinessId) {
                    if (!cancelled) {
                        setPromptBadgeCounts({
                            birthdays: nextBirthdays,
                            expiredDocs: 0,
                            pastDueMedication: 0,
                        });
                    }
                    syncing = false;
                    return;
                }

                const summaryResult = await apolloClient.query({
                    query: GET_BOT_BADGE_SUMMARY,
                    variables: { businessId: numericBusinessId },
                    fetchPolicy: "network-only",
                });
                const summary = summaryResult?.data?.getBotBadgeSummary || {};
                nextBirthdays = Number(summary?.todaysBirthdaysCount || 0);
                nextExpiredDocs = Number(summary?.expiredDocsCount || 0);
                nextPastDueMedication = Number(summary?.pastDueMedicationCount || 0);
            } catch (_error) {
                // Keep local fallback count.
                nextBirthdays = localTodaysBirthdayCount;
                nextExpiredDocs = 0;
                nextPastDueMedication = 0;
            }

            if (!cancelled) {
                setPromptBadgeCounts({
                    birthdays: nextBirthdays,
                    expiredDocs: nextExpiredDocs,
                    pastDueMedication: nextPastDueMedication,
                });
                hasPerformedInitialBadgeSyncRef.current = true;
            }
            syncing = false;
            return;
        };

        if (isOpen) {
            runPromptBadgeSync();
        } else {
            setPromptBadgeCounts((prev) => ({
                ...prev,
                birthdays: localTodaysBirthdayCount,
                expiredDocs: 0,
                pastDueMedication: 0,
            }));
        }

        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen,
        numericBusinessId,
        apolloClient,
        localTodaysBirthdayCount,
    ]);

    const starterPromptBadgeCounts = useMemo(
        () => ({
            "Show today's birthdays": promptBadgeCounts.birthdays,
            "Show expired docs": promptBadgeCounts.expiredDocs,
            "Show my appointments today": todaysAppointmentsCount,
            "Show past due medication": promptBadgeCounts.pastDueMedication,
        }),
        [
            promptBadgeCounts.birthdays,
            promptBadgeCounts.expiredDocs,
            promptBadgeCounts.pastDueMedication,
            todaysAppointmentsCount,
        ]
    );

    useEffect(() => {
        if (!isInline || typeof window === "undefined") return undefined;

        const occupiedWidth = isOpen && useDockedPanel ? INLINE_DOCKED_PANEL_RESERVED_WIDTH : 0;
        window.__planybotInlineOccupiedWidth = occupiedWidth;
        window.dispatchEvent(
            new CustomEvent("planybot:inline-panel-change", {
                detail: { occupiedWidth },
            })
        );

        return () => {
            window.__planybotInlineOccupiedWidth = 0;
            window.dispatchEvent(
                new CustomEvent("planybot:inline-panel-change", {
                    detail: { occupiedWidth: 0 },
                })
            );
        };
    }, [isInline, isOpen, useDockedPanel]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isOpen]);

    useEffect(() => {
        if (!isOpen || disableInput || isLoading || typeof window === "undefined") return undefined;
        const timerId = window.setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(timerId);
    }, [isOpen, disableInput, isLoading, messages.length]);

    useEffect(() => {
        if (!pendingTabNavigation) return undefined;

        let attempts = 0;
        const maxAttempts = 20;
        const timerId = window.setInterval(() => {
            attempts += 1;
            const clicked = clickTabByTarget(pendingTabNavigation);

            if (clicked) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content: `Opened ${pendingTabNavigation.label} tab.`,
                    },
                ]);
                setPendingTabNavigation(null);
                window.clearInterval(timerId);
                return;
            }

            if (attempts >= maxAttempts) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content: `I opened ${pendingTabNavigation.pageLabel}, but could not switch to ${pendingTabNavigation.label} automatically. Please click it once from the side menu.`,
                    },
                ]);
                setPendingTabNavigation(null);
                window.clearInterval(timerId);
            }
        }, 250);

        return () => window.clearInterval(timerId);
    }, [pendingTabNavigation]);

    const sendMessage = async (messageText) => {
        const userMessage = messageText.trim();
        if (!userMessage || disableInput) return;

        const nextMessages = [...messages, { role: "user", content: userMessage }];
        setMessages(nextMessages);
        setInput("");
        setIsLoading(true);

        try {
            const runtimeContext = buildRuntimeContext();
            let localBirthdayReply = "";
            if (isTodaysBirthdayQuestion(userMessage)) {
                let birthdayResidents = Array.isArray(selectedBusiness?.residents)
                    ? selectedBusiness.residents
                    : [];

                if (numericBusinessId) {
                    try {
                        const residentsResult = await apolloClient.query({
                            query: GET_BOT_RESIDENT_BIRTHDAYS,
                            variables: { businessId: numericBusinessId },
                            fetchPolicy: "network-only",
                        });
                        const fetchedResidents = residentsResult?.data?.getResidents;
                        if (Array.isArray(fetchedResidents) && fetchedResidents.length) {
                            birthdayResidents = fetchedResidents;
                        }
                    } catch (_error) {
                        // Keep fallback to resident data in selectedBusiness.
                    }
                }

                localBirthdayReply = buildTodaysBirthdayReply(
                    userMessage,
                    birthdayResidents,
                    selectedBusiness?.businessStaffs || []
                );
            }
            let localExpiredDocsReply = "";
            if (!localBirthdayReply && isExpiredDocumentsQuestion(userMessage)) {
                try {
                    localExpiredDocsReply = await buildCrossPageExpiredDocsReply(
                        userMessage,
                        runtimeContext
                    );
                } catch (error) {
                    localExpiredDocsReply = "";
                }

                if (!localExpiredDocsReply) {
                    localExpiredDocsReply = buildExpiredDocumentsReply(userMessage, runtimeContext);
                }
            }
            const localPlanReply = buildPlanIntentReply(
                userMessage,
                runtimeContext,
                plansData?.plans || [],
                subscription
            );
            const localAppointmentsReply = isTodaysAppointmentsQuestion(userMessage)
                ? (
                    bookingsLoading
                        ? "Loading today's appointments..."
                        : buildTodaysAppointmentsReply(userMessage, expandedBookings, providerUser)
                )
                : "";
            const localConflictReply =
                !localBirthdayReply &&
                    !localExpiredDocsReply &&
                    !localPlanReply &&
                    !localAppointmentsReply
                    ? buildConflictCheckReply(userMessage)
                    : "";
            const localCalendarReply =
                !localBirthdayReply &&
                    !localExpiredDocsReply &&
                    !localPlanReply &&
                    !localAppointmentsReply &&
                    !localConflictReply &&
                    isCalendarNavigationQuestion(userMessage)
                    ? buildCalendarNavigationReply(runtimeContext.pathname)
                    : "";
            const isFollowUpNavigation = isFollowUpNavigationRequest(userMessage);
            const tabNavigationTarget =
                !localBirthdayReply &&
                    !localExpiredDocsReply &&
                    !localPlanReply &&
                    !localAppointmentsReply &&
                    !localConflictReply &&
                    !localCalendarReply
                    ? (
                        isFollowUpNavigation && lastTabNavigationTarget
                            ? lastTabNavigationTarget
                            : resolveTabNavigationTarget(userMessage, runtimeContext.pathname)
                    )
                    : null;
            let localTabNavigationReply = "";
            if (tabNavigationTarget) {
                setLastTabNavigationTarget(tabNavigationTarget);
                const onExpectedPage = String(runtimeContext.pathname || "").startsWith(
                    tabNavigationTarget.route
                );
                const clickedNow = onExpectedPage ? clickTabByTarget(tabNavigationTarget) : false;
                if (clickedNow) {
                    setPendingTabNavigation(null);
                    localTabNavigationReply = `Opened ${tabNavigationTarget.label} tab.`;
                } else {
                    setPendingTabNavigation(tabNavigationTarget);
                    if (!onExpectedPage) {
                        navigate(tabNavigationTarget.route);
                        localTabNavigationReply = `Opening ${tabNavigationTarget.pageLabel}. I will switch to ${tabNavigationTarget.label} tab automatically.`;
                    } else {
                        localTabNavigationReply = `Switching to ${tabNavigationTarget.label} tab...`;
                    }
                }
            }
            const navigationTarget =
                !localBirthdayReply &&
                    !localExpiredDocsReply &&
                    !localPlanReply &&
                    !localAppointmentsReply &&
                    !localConflictReply &&
                    !localCalendarReply &&
                    !localTabNavigationReply
                    ? (
                        isFollowUpNavigation && !tabNavigationTarget && lastPageNavigationTarget
                            ? lastPageNavigationTarget
                            : resolveNavigationTarget(userMessage, runtimeContext.pathname)
                    )
                    : null;
            const localNavigationReply = navigationTarget
                ? buildNavigationReply(navigationTarget, runtimeContext.pathname)
                : "";
            if (navigationTarget) {
                setLastPageNavigationTarget(navigationTarget);
            }
            if (navigationTarget && localNavigationReply.startsWith("Opening ")) {
                navigate(navigationTarget.route);
            }
            let localCreateBookingTypeReply = "";
            if (
                !localBirthdayReply &&
                !localExpiredDocsReply &&
                !localPlanReply &&
                !localCalendarReply &&
                !localTabNavigationReply &&
                !localNavigationReply
            ) {
                if (
                    isConfirmCreateBookingType(userMessage) &&
                    pendingCreateBookingType &&
                    String(pendingCreateBookingType?.wizardStep || "") === "ready"
                ) {
                    const missingForConfirm = [];
                    if (!String(pendingCreateBookingType?.title || "").trim()) {
                        missingForConfirm.push("title");
                    }
                    if (!Number.isFinite(Number(pendingCreateBookingType?.durationMinutes))) {
                        missingForConfirm.push("duration");
                    }
                    if (!Array.isArray(pendingCreateBookingType?.availabilityDays) || !pendingCreateBookingType.availabilityDays.length) {
                        missingForConfirm.push("availability days");
                    }
                    if (!String(pendingCreateBookingType?.availabilityStartLabel || "").trim()) {
                        missingForConfirm.push("start time");
                    }
                    if (!String(pendingCreateBookingType?.availabilityEndLabel || "").trim()) {
                        missingForConfirm.push("end time");
                    }
                    if (!Number.isFinite(Number(pendingCreateBookingType?.bufferMinutes))) {
                        missingForConfirm.push("buffer");
                    }
                    if (missingForConfirm.length) {
                        localCreateBookingTypeReply = `Before creating, I still need: ${missingForConfirm.join(", ")}.`;
                    } else {
                        localCreateBookingTypeReply = await runCreateSimpleBookingType(
                            pendingCreateBookingType
                        );
                        setPendingCreateBookingType(null);
                    }
                } else if (isCreateBookingTypeIntent(userMessage)) {
                    const draft = parseSimpleBookingTypeDraft(userMessage);
                    const hasExplicitTitle = String(draft?.title || "").trim() && draft.title !== "Quick Booking Type";
                    const nextDraft = {
                        ...draft,
                        title: hasExplicitTitle ? draft.title : "Quick Support",
                        durationMinutes: Number(draft.durationMinutes || 30),
                        availabilityDays: [],
                        availabilityStartLabel: "",
                        availabilityEndLabel: "",
                        bufferMinutes: null,
                        wizardStep: "mode",
                    };
                    setPendingCreateBookingType(nextDraft);
                    localCreateBookingTypeReply = buildBookingTypeModePrompt();
                } else if (
                    pendingCreateBookingType &&
                    (
                        (
                            /^\s*2\s*$/.test(String(userMessage || "").toLowerCase()) &&
                            String(pendingCreateBookingType?.wizardStep || "") === "ready"
                        ) ||
                        /^cancel$|cancel create|stop$/i.test(String(userMessage || "").toLowerCase())
                    )
                ) {
                    setPendingCreateBookingType(null);
                    localCreateBookingTypeReply = "Canceled booking type creation.";
                } else if (
                    pendingCreateBookingType &&
                    String(pendingCreateBookingType?.wizardStep || "") === "ready"
                ) {
                    const { draft: updatedDraft, changed } = applyReadyDraftOverrides(
                        pendingCreateBookingType,
                        userMessage
                    );
                    if (changed) {
                        updatedDraft.wizardStep = "ready";
                        setPendingCreateBookingType(updatedDraft);
                        localCreateBookingTypeReply = [
                            "Updated booking type draft:",
                            buildBookingTypeReadyReply(updatedDraft),
                        ].join("\n");
                    } else {
                        localCreateBookingTypeReply = "Reply `1` to confirm create, or `2` to cancel. You can also type changes (example: change duration to 45 minutes).";
                    }
                } else if (
                    pendingCreateBookingType &&
                    isBookingTypeDraftRefinement(userMessage)
                ) {
                    const { draft: refinedDraft, changed } = applyBookingTypeDraftRefinement(
                        pendingCreateBookingType,
                        userMessage
                    );
                    if (changed) {
                        let capturedStructuredAvailability = null;
                        const nextDraft = { ...refinedDraft };
                        const lower = String(userMessage || "").toLowerCase();
                        const explicitDuration =
                            parseDurationFromMessage(userMessage) ??
                            parseDurationMinutes(userMessage, null);
                        const dayList = parseAvailabilityDaysFromMessage(userMessage);
                        const parsedTime = parseTimeFromMessage(userMessage);
                        const parsedBuffer = parseBufferMinutesFromMessage(userMessage);

                        if (nextDraft.wizardStep === "title") {
                            const t = parseTitleFromMessage(userMessage);
                            if (t) {
                                nextDraft.title = t;
                                nextDraft.desc = buildSuggestedBookingDescription(t);
                                nextDraft.wizardStep = "description";
                            }
                        } else if (nextDraft.wizardStep === "description") {
                            const option = pickOptionByNumber(userMessage, ["Use suggested", "Type custom", "Skip and keep generic"]);
                            const raw = String(userMessage || "").trim();
                            if (option === "Use suggested" || option === "Skip and keep generic") {
                                nextDraft.desc = nextDraft.desc || buildSuggestedBookingDescription(nextDraft.title);
                                nextDraft.wizardStep = "duration";
                            } else if (option === "Type custom") {
                                nextDraft.wizardStep = "descriptionCustom";
                            } else if (raw && !/^\d+$/.test(raw)) {
                                nextDraft.originalDesc = raw;
                                nextDraft.suggestedDesc = buildSaferCustomBookingDescription(raw, nextDraft.title);
                                nextDraft.wizardStep = "descriptionReview";
                            }
                        } else if (nextDraft.wizardStep === "descriptionCustom") {
                            const raw = String(userMessage || "").trim();
                            if (raw && !/^\d+$/.test(raw)) {
                                nextDraft.originalDesc = raw;
                                nextDraft.suggestedDesc = buildSaferCustomBookingDescription(raw, nextDraft.title);
                                nextDraft.wizardStep = "descriptionReview";
                            }
                        } else if (nextDraft.wizardStep === "descriptionReview") {
                            const option = pickOptionByNumber(userMessage, ["Use suggested", "Keep original", "Edit again"]);
                            const raw = String(userMessage || "").trim();
                            if (option === "Use suggested") {
                                nextDraft.desc = nextDraft.suggestedDesc || buildSuggestedBookingDescription(nextDraft.title);
                                nextDraft.wizardStep = "duration";
                            } else if (option === "Keep original") {
                                nextDraft.desc = nextDraft.originalDesc || nextDraft.desc || buildSuggestedBookingDescription(nextDraft.title);
                                nextDraft.wizardStep = "duration";
                            } else if (option === "Edit again") {
                                nextDraft.wizardStep = "descriptionCustom";
                            } else if (raw && !/^\d+$/.test(raw)) {
                                nextDraft.suggestedDesc = reviseDescriptionWithInstruction(
                                    nextDraft.suggestedDesc || nextDraft.desc,
                                    raw,
                                    nextDraft.title
                                );
                            }
                        } else if (nextDraft.wizardStep === "duration") {
                            if (Number.isFinite(Number(explicitDuration)) && Number(explicitDuration) > 0) {
                                nextDraft.durationMinutes = Number(explicitDuration);
                                nextDraft.wizardStep = "days";
                            }
                        } else if (nextDraft.wizardStep === "days") {
                            const freeTextSchedule = String(userMessage || "").trim();
                            const structured = parseStructuredAvailabilityFromText(freeTextSchedule);
                            if (structured.entries.length) {
                                const combinedDays = [...new Set(structured.entries.flatMap((entry) => entry.days))];
                                nextDraft.availabilityDays = combinedDays;
                                nextDraft.availabilityStartLabel = structured.entries[0].start;
                                nextDraft.availabilityEndLabel = structured.entries[0].end;
                                nextDraft.availabilityNotes = [
                                    String(nextDraft.availabilityNotes || "").trim(),
                                    `Custom availability schedule:\n${structured.noteBlock}`,
                                ]
                                    .filter(Boolean)
                                    .join("\n");
                                nextDraft.wizardStep = "buffer";
                                capturedStructuredAvailability = structured;
                            } else if (dayList.length) {
                                nextDraft.availabilityDays = dayList;
                                nextDraft.wizardStep = "start";
                            }
                        } else if (nextDraft.wizardStep === "start") {
                            if (parsedTime) {
                                nextDraft.availabilityStartLabel = parsedTime.label;
                                nextDraft.wizardStep = "end";
                            }
                        } else if (nextDraft.wizardStep === "end") {
                            if (/\bmidnight\b/i.test(lower)) {
                                nextDraft.availabilityEndLabel = "11:59 PM";
                                nextDraft.wizardStep = "buffer";
                            } else if (parsedTime) {
                                nextDraft.availabilityEndLabel = parsedTime.label;
                                nextDraft.wizardStep = "buffer";
                            }
                        } else if (nextDraft.wizardStep === "buffer") {
                            if (Number.isFinite(Number(parsedBuffer))) {
                                nextDraft.bufferMinutes = Number(parsedBuffer);
                                nextDraft.wizardStep = "ready";
                            }
                        }

                        setPendingCreateBookingType(nextDraft);
                        if (nextDraft.wizardStep === "description") {
                            localCreateBookingTypeReply = [
                                `Title set to "${nextDraft.title}".`,
                                `Question 2/7: Description suggestion:\n"${nextDraft.desc || buildSuggestedBookingDescription(nextDraft.title)}"`,
                                buildNumberedQuestion(
                                    "Use it as-is or update it:",
                                    ["Use suggested", "Type custom", "Skip and keep generic"]
                                ),
                            ].join("\n");
                        } else if (nextDraft.wizardStep === "descriptionCustom") {
                            localCreateBookingTypeReply = "Type your custom description now, then I will continue.";
                        } else if (nextDraft.wizardStep === "descriptionReview") {
                            localCreateBookingTypeReply = [
                                `Suggested description:\n"${nextDraft.suggestedDesc || buildSuggestedBookingDescription(nextDraft.title)}"`,
                                buildNumberedQuestion(
                                    "Choose one:",
                                    ["Use suggested", "Keep original", "Edit again"]
                                ),
                            ].join("\n");
                        } else if (nextDraft.wizardStep === "duration") {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 3/7: How long should each booking be?",
                                BOOKING_DURATION_OPTIONS.map((m) => `${m} minutes`)
                            );
                        } else if (nextDraft.wizardStep === "days") {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 4/7: Which days should be available?",
                                BOOKING_DAYS_OPTIONS.map((item) => item.label)
                            );
                        } else if (nextDraft.wizardStep === "start") {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 5/7: What is the daily start time?",
                                BOOKING_START_OPTIONS
                            );
                        } else if (nextDraft.wizardStep === "end") {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 6/7: What is the daily end time?",
                                BOOKING_END_OPTIONS
                            );
                        } else if (nextDraft.wizardStep === "buffer") {
                            if (capturedStructuredAvailability) {
                                localCreateBookingTypeReply = [
                                    "Captured your custom availability windows:",
                                    `- Days: ${nextDraft.availabilityDays.join(", ")}`,
                                    `- Default time window: ${nextDraft.availabilityStartLabel} to ${nextDraft.availabilityEndLabel}`,
                                    `- Structured windows: ${capturedStructuredAvailability.summary}`,
                                    buildBufferStepPrompt(),
                                ].join("\n");
                            } else {
                                localCreateBookingTypeReply = buildBufferStepPrompt();
                            }
                        } else {
                            localCreateBookingTypeReply = buildBookingTypeReadyReply(nextDraft);
                        }
                    }
                } else if (pendingCreateBookingType) {
                    const nextDraft = { ...pendingCreateBookingType };
                    const lower = String(userMessage || "").toLowerCase();
                    const explicitDuration =
                        parseDurationFromMessage(userMessage) ??
                        parseDurationMinutes(userMessage, null);
                    const dayList = parseAvailabilityDaysFromMessage(userMessage);
                    const parsedTime = parseTimeFromMessage(userMessage);
                    const parsedBuffer = parseBufferMinutesFromMessage(userMessage);

                    if (nextDraft.wizardStep === "mode") {
                        if (/^\s*1\s*$/.test(lower) || /\bquick\b|\bdefault\b|\brush\b/.test(lower)) {
                            nextDraft.title = nextDraft.title || "Quick Support";
                            nextDraft.durationMinutes = 30;
                            nextDraft.availabilityDays = [
                                "Monday",
                                "Tuesday",
                                "Wednesday",
                                "Thursday",
                                "Friday",
                                "Saturday",
                                "Sunday",
                            ];
                            nextDraft.availabilityStartLabel = "9:00 AM";
                            nextDraft.availabilityEndLabel = "5:00 PM";
                            nextDraft.bufferMinutes = 0;
                            nextDraft.wizardStep = "ready";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildBookingTypeReadyReply(nextDraft, { quick: true });
                        } else if (/^\s*2\s*$/.test(lower) || /\bguided\b|\bstep\b|\bcustom\b/.test(lower)) {
                            nextDraft.wizardStep = "title";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 1/7: Choose booking type title:",
                                BOOKING_TITLE_OPTIONS
                            );
                        } else {
                            localCreateBookingTypeReply = "Reply `1` for quick default, or `2` for guided setup.";
                        }
                    } else if (nextDraft.wizardStep === "title") {
                        const selectedTitle = pickOptionByNumber(userMessage, BOOKING_TITLE_OPTIONS);
                        const t = selectedTitle || parseTitleFromMessage(userMessage);
                        if (t) {
                            nextDraft.title = t;
                            nextDraft.desc = buildSuggestedBookingDescription(t);
                            nextDraft.wizardStep = "description";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = [
                                `Question 2/7: Description suggestion:\n"${nextDraft.desc}"`,
                                buildNumberedQuestion(
                                    "Use it as-is or update it:",
                                    ["Use suggested", "Type custom", "Skip and keep generic"]
                                ),
                            ].join("\n");
                        } else {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Please select a title using number:",
                                BOOKING_TITLE_OPTIONS
                            );
                        }
                    } else if (nextDraft.wizardStep === "description") {
                        const option = pickOptionByNumber(userMessage, ["Use suggested", "Type custom", "Skip and keep generic"]);
                        const raw = String(userMessage || "").trim();
                        if (option === "Use suggested" || option === "Skip and keep generic") {
                            nextDraft.desc = nextDraft.desc || buildSuggestedBookingDescription(nextDraft.title);
                            nextDraft.wizardStep = "duration";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 3/7: How long should each booking be?",
                                BOOKING_DURATION_OPTIONS.map((m) => `${m} minutes`)
                            );
                        } else if (option === "Type custom") {
                            nextDraft.wizardStep = "descriptionCustom";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = "Type your custom description now.";
                        } else if (raw && !/^\d+$/.test(raw)) {
                            nextDraft.originalDesc = raw;
                            nextDraft.suggestedDesc = buildSaferCustomBookingDescription(raw, nextDraft.title);
                            nextDraft.wizardStep = "descriptionReview";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = [
                                `Suggested description:\n"${nextDraft.suggestedDesc}"`,
                                buildNumberedQuestion(
                                    "Choose one:",
                                    ["Use suggested", "Keep original", "Edit again"]
                                ),
                            ].join("\n");
                        } else {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Choose description option:",
                                ["Use suggested", "Type custom", "Skip and keep generic"]
                            );
                        }
                    } else if (nextDraft.wizardStep === "descriptionCustom") {
                        const raw = String(userMessage || "").trim();
                        if (raw && !/^\d+$/.test(raw)) {
                            nextDraft.originalDesc = raw;
                            nextDraft.suggestedDesc = buildSaferCustomBookingDescription(raw, nextDraft.title);
                            nextDraft.wizardStep = "descriptionReview";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = [
                                `Suggested description:\n"${nextDraft.suggestedDesc}"`,
                                buildNumberedQuestion(
                                    "Choose one:",
                                    ["Use suggested", "Keep original", "Edit again"]
                                ),
                            ].join("\n");
                        } else {
                            localCreateBookingTypeReply = "Please type a custom description.";
                        }
                    } else if (nextDraft.wizardStep === "descriptionReview") {
                        const option = pickOptionByNumber(userMessage, ["Use suggested", "Keep original", "Edit again"]);
                        const raw = String(userMessage || "").trim();
                        if (option === "Use suggested") {
                            nextDraft.desc = nextDraft.suggestedDesc || buildSuggestedBookingDescription(nextDraft.title);
                            nextDraft.wizardStep = "duration";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 3/7: How long should each booking be?",
                                BOOKING_DURATION_OPTIONS.map((m) => `${m} minutes`)
                            );
                        } else if (option === "Keep original") {
                            nextDraft.desc = nextDraft.originalDesc || nextDraft.desc || buildSuggestedBookingDescription(nextDraft.title);
                            nextDraft.wizardStep = "duration";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 3/7: How long should each booking be?",
                                BOOKING_DURATION_OPTIONS.map((m) => `${m} minutes`)
                            );
                        } else if (option === "Edit again") {
                            nextDraft.wizardStep = "descriptionCustom";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = "Type your custom description now.";
                        } else if (raw && !/^\d+$/.test(raw)) {
                            nextDraft.suggestedDesc = reviseDescriptionWithInstruction(
                                nextDraft.suggestedDesc || nextDraft.desc,
                                raw,
                                nextDraft.title
                            );
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = [
                                `Updated suggested description:\n"${nextDraft.suggestedDesc}"`,
                                buildNumberedQuestion(
                                    "Choose one:",
                                    ["Use suggested", "Keep original", "Edit again"]
                                ),
                            ].join("\n");
                        } else {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Choose one:",
                                ["Use suggested", "Keep original", "Edit again"]
                            );
                        }
                    } else if (nextDraft.wizardStep === "duration") {
                        const selectedDuration = pickOptionByNumber(userMessage, BOOKING_DURATION_OPTIONS);
                        const finalDuration = Number(selectedDuration ?? explicitDuration);
                        if (Number.isFinite(finalDuration) && finalDuration > 0) {
                            nextDraft.durationMinutes = finalDuration;
                            nextDraft.wizardStep = "days";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 4/7: Which days should be available?",
                                BOOKING_DAYS_OPTIONS.map((item) => item.label)
                            );
                        } else {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Please select duration using number:",
                                BOOKING_DURATION_OPTIONS.map((m) => `${m} minutes`)
                            );
                        }
                    } else if (nextDraft.wizardStep === "days") {
                        const selectedDays = pickOptionByNumber(userMessage, BOOKING_DAYS_OPTIONS);
                        const resolvedDays = selectedDays?.days || dayList;
                        const freeTextSchedule = String(userMessage || "").trim();
                        const structured = parseStructuredAvailabilityFromText(freeTextSchedule);
                        if (!selectedDays && structured.entries.length) {
                            const combinedDays = [...new Set(structured.entries.flatMap((entry) => entry.days))];
                            nextDraft.availabilityDays = combinedDays;
                            nextDraft.availabilityStartLabel = structured.entries[0].start;
                            nextDraft.availabilityEndLabel = structured.entries[0].end;
                            nextDraft.availabilityNotes = [
                                String(nextDraft.availabilityNotes || "").trim(),
                                `Custom availability schedule:\n${structured.noteBlock}`,
                            ]
                                .filter(Boolean)
                                .join("\n");
                            nextDraft.wizardStep = "buffer";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = [
                                "Captured your custom availability windows.",
                                `- Days: ${nextDraft.availabilityDays.join(", ")}`,
                                `- Default time window: ${nextDraft.availabilityStartLabel} to ${nextDraft.availabilityEndLabel}`,
                                `- Structured windows: ${structured.summary}`,
                                "I saved your full schedule text in availability notes for per-day adjustments.",
                                buildBufferStepPrompt(),
                            ].join("\n");
                        } else if (resolvedDays.length) {
                            nextDraft.availabilityDays = resolvedDays;
                            nextDraft.wizardStep = "start";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 5/7: What is the daily start time?",
                                BOOKING_START_OPTIONS
                            );
                        } else {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Please select available days using number:",
                                BOOKING_DAYS_OPTIONS.map((item) => item.label)
                            );
                        }
                    } else if (nextDraft.wizardStep === "start") {
                        const selectedStart = pickOptionByNumber(userMessage, BOOKING_START_OPTIONS);
                        if (selectedStart || parsedTime) {
                            nextDraft.availabilityStartLabel = selectedStart || parsedTime.label;
                            nextDraft.wizardStep = "end";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 6/7: What is the daily end time?",
                                BOOKING_END_OPTIONS
                            );
                        } else {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Please select start time using number:",
                                BOOKING_START_OPTIONS
                            );
                        }
                    } else if (nextDraft.wizardStep === "end") {
                        const selectedEnd = pickOptionByNumber(userMessage, BOOKING_END_OPTIONS);
                        if (selectedEnd || /\bmidnight\b/i.test(lower) || parsedTime) {
                            nextDraft.availabilityEndLabel =
                                selectedEnd ||
                                (/\bmidnight\b/i.test(lower) ? "11:59 PM" : parsedTime.label);
                            nextDraft.wizardStep = "buffer";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildBufferStepPrompt();
                        } else {
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Please select end time using number:",
                                BOOKING_END_OPTIONS
                            );
                        }
                    } else if (nextDraft.wizardStep === "buffer") {
                        if (/^\s*6\s*$/.test(lower) || /\bedit\s+schedule\b/.test(lower)) {
                            nextDraft.wizardStep = "days";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildNumberedQuestion(
                                "Question 4/7: Which days should be available?",
                                BOOKING_DAYS_OPTIONS.map((item) => item.label)
                            );
                            return;
                        }
                        const selectedBuffer = pickOptionByNumber(userMessage, BOOKING_BUFFER_OPTIONS);
                        const resolvedBuffer = Number(selectedBuffer ?? parsedBuffer);
                        if (Number.isFinite(resolvedBuffer)) {
                            nextDraft.bufferMinutes = resolvedBuffer;
                            nextDraft.wizardStep = "ready";
                            setPendingCreateBookingType(nextDraft);
                            localCreateBookingTypeReply = buildBookingTypeReadyReply(nextDraft);
                        } else {
                            localCreateBookingTypeReply = buildBufferStepPrompt();
                        }
                    } else {
                        localCreateBookingTypeReply = "Reply `1` to confirm create, or `2` to cancel.";
                    }
                }
            }
            const localBookForMeReply =
                !localBirthdayReply &&
                    !localExpiredDocsReply &&
                    !localPlanReply &&
                    !localCalendarReply &&
                    !localConflictReply &&
                    !localTabNavigationReply &&
                    !localNavigationReply &&
                    !localCreateBookingTypeReply
                    ? await buildBookForMeReply(userMessage)
                    : "";
            const localInviteLinkReply =
                !localBirthdayReply &&
                    !localExpiredDocsReply &&
                    !localPlanReply &&
                    !localCalendarReply &&
                    !localConflictReply &&
                    !localTabNavigationReply &&
                    !localNavigationReply &&
                    !localCreateBookingTypeReply &&
                    !localBookForMeReply
                    ? await runSendInviteLinkFlow(userMessage)
                    : "";
            const localScopedReply =
                !localBirthdayReply &&
                    !localPlanReply &&
                    !localAppointmentsReply &&
                    !localConflictReply &&
                    !localCalendarReply &&
                    !localTabNavigationReply &&
                    !localNavigationReply &&
                    !localCreateBookingTypeReply &&
                    !localBookForMeReply &&
                    !localInviteLinkReply &&
                    !localExpiredDocsReply &&
                    isPageScopeQuestion(userMessage)
                    ? buildScopedPageReply(
                        runtimeContext.routeContext,
                        runtimeContext.activeTabContext,
                        runtimeContext.activeFieldLabel,
                        runtimeContext.pathname,
                        runtimeContext.headings,
                        runtimeContext.visibleActionButtons
                    )
                    : "";
            const workflowMatch =
                !localBirthdayReply &&
                    !localPlanReply &&
                    !localAppointmentsReply &&
                    !localConflictReply &&
                    !localCalendarReply &&
                    !localTabNavigationReply &&
                    !localNavigationReply &&
                    !localCreateBookingTypeReply &&
                    !localBookForMeReply &&
                    !localInviteLinkReply &&
                    !localScopedReply &&
                    !localExpiredDocsReply &&
                    isWorkflowQuestion(userMessage)
                    ? findWorkflowMatch(runtimeContext.routeContext, userMessage)
                    : null;
            const localWorkflowReply = workflowMatch
                ? buildWorkflowReply(workflowMatch, runtimeContext)
                : "";

            if (
                localBirthdayReply ||
                localExpiredDocsReply ||
                localPlanReply ||
                localAppointmentsReply ||
                localConflictReply ||
                localCalendarReply ||
                localTabNavigationReply ||
                localNavigationReply ||
                localCreateBookingTypeReply ||
                localBookForMeReply ||
                localInviteLinkReply ||
                localScopedReply ||
                localWorkflowReply
            ) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content:
                            localBirthdayReply ||
                            localExpiredDocsReply ||
                            localPlanReply ||
                            localAppointmentsReply ||
                            localConflictReply ||
                            localCalendarReply ||
                            localTabNavigationReply ||
                            localNavigationReply ||
                            localCreateBookingTypeReply ||
                            localBookForMeReply ||
                            localInviteLinkReply ||
                            localScopedReply ||
                            localWorkflowReply,
                    },
                ]);
                return;
            }

            const client = getOpenAIClient();
            if (!client) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content:
                            "AI is not configured yet. Please enable the server AI endpoint and try again.",
                    },
                ]);
                return;
            }

            const response = await client.chat.completions.create({
                model: "gpt-4o-mini",
                temperature: 0.5,
                messages: [
                    { role: "system", content: BOT_SYSTEM_PROMPT },
                    runtimeContext.text
                        ? {
                            role: "system",
                            content: `Current in-app context (use this first when answering):\n${runtimeContext.text}`,
                        }
                        : null,
                    ...nextMessages.map((msg) => ({
                        role: msg.role,
                        content: msg.content,
                    })),
                ].filter(Boolean),
            });

            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: parseReplyContent(response),
                },
            ]);
        } catch (error) {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "I hit an issue reaching the assistant. Please try again in a moment.",
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        await sendMessage(input);
    };

    const startFromPrompt = async (prompt) => {
        await sendMessage(prompt);
    };

    const resolveDocumentTabTargetFromLink = (linkValue = "") => {
        const value = String(linkValue || "").toLowerCase();
        if (value.startsWith("/biz/business#documents")) {
            return {
                route: "/biz/business",
                pageLabel: "Business settings page",
                label: "Documents",
                aliases: ["Documents", "Business Documents"],
            };
        }
        if (value.startsWith("/biz/resident#documents")) {
            return {
                route: "/biz/resident",
                pageLabel: "Resident page",
                label: "Documents",
                aliases: ["Documents", "Resident Documents"],
            };
        }
        if (value.startsWith("/biz/staff#document")) {
            return {
                route: "/biz/staff",
                pageLabel: "Staff page",
                label: "Document",
                aliases: ["Document", "Documents", "Staff Document"],
            };
        }
        return null;
    };

    const renderMessageContent = (content) =>
        splitMessageLinks(content).map((segment, index) => {
            if (segment.type === "text") {
                return (
                    <React.Fragment key={`txt-${index}`}>
                        {segment.value}
                    </React.Fragment>
                );
            }

            const linkValue = segment.value;
            const linkLabel = segment.label || linkValue;
            const isInternalRoute = linkValue.startsWith("/");

            return (
                <MuiLink
                    key={`lnk-${index}`}
                    href={linkValue}
                    color="inherit"
                    underline="always"
                    target={isInternalRoute ? undefined : "_blank"}
                    rel={isInternalRoute ? undefined : "noopener noreferrer"}
                    onClick={(event) => {
                        if (!isInternalRoute) return;
                        event.preventDefault();
                        const tabTarget = resolveDocumentTabTargetFromLink(linkValue);
                        if (tabTarget) {
                            setLastTabNavigationTarget(tabTarget);
                            const currentPath =
                                typeof window !== "undefined"
                                    ? String(window.location.pathname || "")
                                    : "";
                            const onExpectedPage = currentPath.startsWith(tabTarget.route);
                            const clickedNow = onExpectedPage ? clickTabByTarget(tabTarget) : false;
                            if (clickedNow) {
                                setPendingTabNavigation(null);
                                return;
                            }
                            setPendingTabNavigation(tabTarget);
                            if (!onExpectedPage) {
                                navigate(tabTarget.route);
                            }
                            return;
                        }
                        navigate(linkValue);
                    }}
                    sx={{ fontWeight: 600 }}
                >
                    {linkLabel}
                </MuiLink>
            );
        });

    return (
        <Box
            data-planybot-root="true"
            sx={{
                position: isInline ? "relative" : "fixed",
                right: isInline ? "auto" : { xs: 14, sm: 22 },
                bottom: isInline ? "auto" : { xs: 14, sm: 22 },
                zIndex: isInline ? 2200 : 2100,
                display: "inline-flex",
                alignItems: "center",
            }}
        >
            {checkPlanNeeded && (
                <UpgradePlan
                    featureName="PlanyCare Assistant"
                    requiredPlanId={REQUIRED_PREMIUM_PLAN_LEVEL}
                    onClose={() => setCheckPlanNeeded(false)}
                />
            )}

            {!isOpen && (
                <Tooltip title="Chat with PlanyCare Assistant" placement={isInline ? "bottom" : "left"}>
                    <Fab
                        size={isInline ? "small" : "large"}
                        sx={{
                            bgcolor: CUSTOMER_HEADER_MAIN_BG,
                            color: "#fff",
                            "&:hover": { bgcolor: "#1a2b3a" },
                            ...(isInline
                                ? {
                                    width: 34,
                                    height: 34,
                                    minHeight: 34,
                                }
                                : {}),
                        }}
                        onClick={() => {
                            // Always open the panel so users can see status/messages.
                            // Plan gating is enforced via disabled input and upgrade prompts.
                            setIsOpen(true);
                        }}
                        aria-label="Open planyBot chat"
                    >
                        <PlanyBotSparkIcon size={isInline ? 18 : 24} />
                    </Fab>
                </Tooltip>
            )}

            {isOpen && (
                <Paper
                    elevation={8}
                    sx={{
                        width: useDockedPanel
                            ? INLINE_DOCKED_PANEL_WIDTH
                            : useCompactInlinePanel
                                ? { xs: "calc(100vw - 16px)", sm: 360 }
                                : { xs: "calc(100vw - 28px)", sm: 360 },
                        maxWidth: "100vw",
                        height: useDockedPanel
                            ? { xs: "calc(100vh - 84px)", sm: "calc(100vh - 108px)" }
                            : useCompactInlinePanel
                                ? "52vh"
                                : "auto",
                        minHeight: useDockedPanel
                            ? { xs: 360, sm: 460 }
                            : useCompactInlinePanel
                                ? 300
                                : "auto",
                        borderRadius: 2,
                        overflowX: "hidden",
                        overflowY: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        ...(useDockedPanel
                            ? {
                                position: "fixed",
                                right: 0,
                                top: { xs: 74, sm: 94 },
                                zIndex: 2600,
                                boxShadow: "0 22px 48px rgba(15, 23, 42, 0.24)",
                            }
                            : useCompactInlinePanel
                                ? {
                                    position: "fixed",
                                    left: { xs: 8, sm: "auto" },
                                    right: { xs: 8, sm: 16 },
                                    bottom: { xs: 10, sm: 12 },
                                    top: "auto",
                                    zIndex: 2600,
                                    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.22)",
                                }
                                : isInline
                                    ? {
                                        position: "absolute",
                                        right: 0,
                                        top: "calc(100% + 8px)",
                                        zIndex: 2201,
                                    }
                                    : {}),
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            px: 1.5,
                            py: 1,
                            bgcolor: CUSTOMER_HEADER_MAIN_BG,
                            color: "#fff",
                        }}
                    >
                        <Stack direction="row" spacing={0.9} alignItems="center">
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: "50%",
                                    display: "grid",
                                    placeItems: "center",
                                    background: CUSTOMER_HEADER_MAIN_BG,
                                    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.22)",
                                    border: "1px solid rgba(255,255,255,0.28)",
                                }}
                            >
                                <PlanyBotSparkIcon size={14} />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                PlanyCare Assistant
                            </Typography>
                        </Stack>
                        <IconButton
                            size="small"
                            onClick={() => setIsOpen(false)}
                            sx={{ color: "#fff" }}
                            aria-label="Close planyBot chat"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Box
                        ref={scrollRef}
                        sx={{
                            height: useDockedPanel || useCompactInlinePanel ? "auto" : 350,
                            minHeight: useDockedPanel || useCompactInlinePanel ? 240 : 350,
                            flex: useDockedPanel || useCompactInlinePanel ? 1 : "0 0 auto",
                            overflowY: "auto",
                            minWidth: 0,
                            px: 1.5,
                            py: 1.25,
                            bgcolor: "#f8fafc",
                        }}
                    >
                        <Stack spacing={1}>
                            {messages.map((message, index) => (
                                <Box
                                    key={`${message.role}-${index}`}
                                    sx={{
                                        alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                                        maxWidth: "86%",
                                        px: 1.2,
                                        py: 0.9,
                                        borderRadius: 1.5,
                                        bgcolor: message.role === "user" ? "#1d4ed8" : "#e2e8f0",
                                        color: message.role === "user" ? "#fff" : "#0f172a",
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                        {renderMessageContent(message.content)}
                                    </Typography>
                                </Box>
                            ))}

                            {isLoading && (
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="caption">Thinking...</Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            px: 1.25,
                            pb: 1,
                            pt: 0.8,
                            borderTop: "1px solid #e2e8f0",
                            flexWrap: "wrap",
                            rowGap: 0.75,
                        }}
                    >
                        {STARTER_PROMPTS.slice(0, 4).map((prompt) => {
                            const promptBadgeCount = Number(starterPromptBadgeCounts[prompt] || 0);
                            return (
                                <Typography
                                    key={prompt}
                                    onClick={() => startFromPrompt(prompt)}
                                    sx={{
                                        cursor: disableInput ? "not-allowed" : "pointer",
                                        fontSize: 11,
                                        lineHeight: 1.2,
                                        px: 1,
                                        py: 0.6,
                                        borderRadius: 4,
                                        bgcolor: "#eef2ff",
                                        color: "#1e3a8a",
                                        opacity: disableInput ? 0.5 : 1,
                                    }}
                                >
                                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}>
                                        <span>{prompt}</span>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                minWidth: 16,
                                                height: 16,
                                                borderRadius: 999,
                                                px: 0.4,
                                                bgcolor: promptBadgeCount > 0 ? "#dc2626" : "#94a3b8",
                                                color: "#fff",
                                                fontSize: 10,
                                                fontWeight: 700,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {promptBadgeCount > 99 ? "99+" : promptBadgeCount}
                                        </Box>
                                    </Box>
                                </Typography>
                            );
                        })}
                    </Stack>

                    {!hasServerAi && (
                        <Typography variant="caption" color="error" sx={{ px: 1.5, pb: 1, display: "block" }}>
                            Server AI endpoint is not configured for planyBot replies.
                        </Typography>
                    )}
                    {checkingPremium && (
                        <Typography variant="caption" sx={{ px: 1.5, pb: 1, display: "block" }}>
                            Checking premium subscription...
                        </Typography>
                    )}
                    {shouldRequireUpgrade && (
                        <Typography variant="caption" color="error" sx={{ px: 1.5, pb: 1, display: "block" }}>
                            Premium billing plan required to use PlanyCare Assistant features.
                        </Typography>
                    )}

                    <Box component="form" onSubmit={onSubmit} sx={{ px: 1.25, pb: 1.25 }}>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Ask PlanyCare Assistant what you want to do..."
                                value={input}
                                inputRef={inputRef}
                                disabled={disableInput}
                                onChange={(event) => setInput(event.target.value)}
                            />
                            <IconButton
                                type="submit"
                                color="primary"
                                disabled={!canSend}
                                aria-label="Send message"
                            >
                                <SendIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}



